  const RECEIPT_STORAGE_KEY = "community-pulse-receipts-v1";
  const RECEIPT_TTL_MS = 7 * 24 * 60 * 60 * 1000;
  const MAX_RECEIPTS = 20;
  const CATEGORY_ORDER = ["people", "animals", "environment"];
  const FEELINGS = new Set(["sick", "good"]);
  const SEVERITIES = new Set(["mild", "moderate", "severe"]);
  const ANIMAL_TYPES = new Set(["bird", "mammal", "reptile", "fish", "amphibian", "other"]);
  const ANIMAL_GROUPS = new Set(["pet", "wildlife", "livestock"]);
  const COUNT_BUCKETS = new Set(["0", "1-5", "6-20", "21_plus"]);
  const PIMA_COUNTY_ZIP_CODES = new Set([
    "85601",
    "85614",
    "85619",
    "85622",
    "85629",
    "85633",
    "85634",
    "85637",
    "85641",
    "85645",
    "85652",
    "85653",
    "85654",
    "85658",
    "85701",
    "85704",
    "85705",
    "85706",
    "85707",
    "85708",
    "85710",
    "85711",
    "85712",
    "85713",
    "85714",
    "85715",
    "85716",
    "85718",
    "85719",
    "85730",
    "85735",
    "85736",
    "85737",
    "85739",
    "85741",
    "85742",
    "85743",
    "85745",
    "85746",
    "85747",
    "85748",
    "85749",
    "85750",
    "85755",
    "85756",
    "85757",
  ]);
  const MARICOPA_COUNTY_ZIP_CODES = new Set([
    ...Array.from({ length: 99 }, (_, index) => String(85001 + index)),
    "85301",
  ]);
  const SYMPTOM_VALUES = new Map([
    ["cough", "cough"],
    ["cough/congestion", "cough"],
    ["congestion", "congestion"],
    ["nausea", "nausea"],
    ["difficulty breathing", "difficulty_breathing"],
    ["sore throat", "sore_throat"],
    ["rash", "rash"],
    ["fever", "fever"],
    ["chills", "chills"],
    ["diarrhea", "diarrhea"],
    ["bleeding from body openings", "bleeding"],
    ["red eyes", "red_eyes"],
    ["body aches", "body_aches"],
    ["headache", "headache"],
    ["discolored or bloody urine", "discolored_or_bloody_urine"],
    ["loss of smell or taste", "loss_of_smell_or_taste"],
    ["yellow skin/eyes", "yellow_skin_or_eyes"],
    ["animal bite", "animal_bite"],
    ["bug bite", "bug_bite"],
    ["other", "other"],
  ]);
  const ENVIRONMENT_VALUES = new Map([
    ["unusual mosquito activity", "vector_activity"],
    ["vector activity", "vector_activity"],
    ["insect vector: flea", "vector_activity"],
    ["insect vector: tick", "vector_activity"],
    ["insect vector: mosquito", "vector_activity"],
    ["insect vector: other", "vector_activity"],
    ["fleas", "vector_activity"],
    ["ticks", "vector_activity"],
    ["mosquitoes", "vector_activity"],
    ["other vector", "vector_activity"],
    ["water contamination", "water_contamination"],
    ["contaminated water", "water_contamination"],
    ["raw sewage", "raw_sewage"],
    ["air quality", "air_quality"],
    ["air quality concerns", "air_quality"],
    ["flooding", "flooding"],
    ["plant or crop damage", "plant_or_crop_damage"],
    ["other", "other"],
    ["other environmental concern", "other"],
  ]);
  const ANIMAL_ICONS = {
    livestock: "🐮",
    wildlife: "🦌",
    default: "🐾",
  };
  const MOBILE_ANIMAL_ICONS = {
    livestock: "🐮",
    wildlife: "🦌",
    default: "🐾",
  };

  function buildOptimisticSummary(payload) {
    const categories = allowedList(payload?.category || payload?.categories, new Map(CATEGORY_ORDER.map((item) => [item, item])));
    const feeling = categories.includes("people") ? allowedSetValue(payload?.feeling, FEELINGS) : null;
    return {
      categories,
      time_bucket: "just_now",
      location: optimisticLocation(payload),
      feeling,
      severity: feeling === "sick" ? allowedSetValue(payload?.severity, SEVERITIES) : null,
      symptoms: feeling === "sick" ? allowedList(payload?.symptoms, SYMPTOM_VALUES) : [],
      animal_type: allowedSetValue(payload?.animal_type, ANIMAL_TYPES),
      animal_group: allowedSetValue(payload?.animal_group, ANIMAL_GROUPS),
      animal_sick_count_bucket: countBucket(payload?.sick_count),
      animal_dead_count_bucket: countBucket(payload?.dead_count),
      environment_concerns: environmentConcernsForSummary(payload, categories),
    };
  }

  function optimisticLocation(payload) {
    const county = countyNameForZipCode(payload?.zip_code);
    return county
      ? { level: "county", label: `${county} County` }
      : { level: "state", label: "Arizona" };
  }

  function environmentConcernsForSummary(payload, categories) {
    if (!categories.includes("environment")) return [];
    if (payload?.environment_issue_type === "insect_vectors") {
      return ["flea", "tick", "mosquito", "other"].includes(payload?.insect_vector_type)
        ? ["vector_activity"]
        : [];
    }
    if (payload?.environment_issue_type === "environmental_concern") {
      return allowedList(payload?.environment_concerns, ENVIRONMENT_VALUES);
    }
    const legacyValues = Array.isArray(payload?.environment_observations)
      ? payload.environment_observations
      : payload?.observations;
    return allowedList(legacyValues, ENVIRONMENT_VALUES);
  }

  function countyNameForZipCode(value) {
    const zipCode = String(value || "").trim();
    if (PIMA_COUNTY_ZIP_CODES.has(zipCode)) return "Pima";
    if (MARICOPA_COUNTY_ZIP_CODES.has(zipCode)) return "Maricopa";
    return null;
  }

  function sanitizeSummary(summary) {
    const categories = allowedList(summary?.categories, new Map(CATEGORY_ORDER.map((item) => [item, item])));
    const feeling = categories.includes("people") ? allowedSetValue(summary?.feeling, FEELINGS) : null;
    return {
      categories,
      time_bucket: sanitizeTimeBucket(summary?.time_bucket),
      location: sanitizeLocation(summary?.location),
      feeling,
      severity: feeling === "sick" ? allowedSetValue(summary?.severity, SEVERITIES) : null,
      symptoms: feeling === "sick" ? allowedList(summary?.symptoms, SYMPTOM_VALUES) : [],
      animal_type: allowedSetValue(summary?.animal_type, ANIMAL_TYPES),
      animal_group: allowedSetValue(summary?.animal_group, ANIMAL_GROUPS),
      animal_sick_count_bucket: allowedLiteralValue(summary?.animal_sick_count_bucket, COUNT_BUCKETS),
      animal_dead_count_bucket: allowedLiteralValue(summary?.animal_dead_count_bucket, COUNT_BUCKETS),
      environment_concerns: allowedList(summary?.environment_concerns, ENVIRONMENT_VALUES),
    };
  }

  function createReceipt(payload, nowMs = Date.now()) {
    return {
      created_at: Math.floor(nowMs / 60000) * 60000,
      summary: buildOptimisticSummary(payload),
    };
  }

  function loadReceipts(storage, nowMs = Date.now()) {
    try {
      const parsed = JSON.parse(storage.getItem(RECEIPT_STORAGE_KEY) || "[]");
      if (!Array.isArray(parsed)) return [];
      return parsed
        .filter((receipt) => (
          Number.isFinite(receipt?.created_at) &&
          receipt.created_at <= nowMs &&
          receipt.created_at >= nowMs - RECEIPT_TTL_MS
        ))
        .map((receipt) => ({
          created_at: receipt.created_at,
          summary: sanitizeSummary(receipt.summary),
        }))
        .filter((receipt) => receipt.summary.categories.length > 0)
        .sort((left, right) => right.created_at - left.created_at)
        .slice(0, MAX_RECEIPTS);
    } catch {
      return [];
    }
  }

  function saveReceipts(storage, receipts) {
    const safeReceipts = receipts
      .filter((receipt) => Number.isFinite(receipt?.created_at))
      .map((receipt) => ({
        created_at: receipt.created_at,
        summary: sanitizeSummary(receipt.summary),
      }))
      .filter((receipt) => receipt.summary.categories.length > 0)
      .sort((left, right) => right.created_at - left.created_at)
      .slice(0, MAX_RECEIPTS);
    storage.setItem(RECEIPT_STORAGE_KEY, JSON.stringify(safeReceipts));
    return safeReceipts;
  }

  function prependReceipts(storage, currentReceipts, payloads, nowMs = Date.now()) {
    const additions = payloads.map((payload, index) => createReceipt(payload, nowMs - index));
    return saveReceipts(storage, [...additions, ...currentReceipts]);
  }

  function reconcileReceipts(receipts, backendItems, nowMs = Date.now()) {
    const safeBackendItems = Array.isArray(backendItems)
      ? backendItems.map(sanitizeSummary).filter((item) => item.categories.length > 0)
      : [];
    const reconciledBackendItems = safeBackendItems.map((item) => ({ ...item, is_own: false }));
    const consumed = new Set();
    const updatedReceipts = receipts.map((receipt) => {
      const matchIndex = safeBackendItems.findIndex(
        (item, index) => !consumed.has(index) && receiptMatchesBackendItem(receipt, item, nowMs)
      );
      if (matchIndex === -1) return receipt;
      consumed.add(matchIndex);
      reconciledBackendItems[matchIndex].is_own = true;
      reconciledBackendItems[matchIndex].receipt_created_at = receipt.created_at;
      return {
        ...receipt,
        summary: safeBackendItems[matchIndex],
      };
    });
    return {
      receipts: updatedReceipts,
      backendItems: reconciledBackendItems,
    };
  }

  function receiptMatchesBackendItem(receipt, backendItem, nowMs) {
    if (summaryFingerprint(receipt.summary) !== summaryFingerprint(backendItem)) return false;
    if (!Number.isFinite(receipt?.created_at)) return false;
    return timeBucketMatchesAge(backendItem.time_bucket, Math.max(0, nowMs - receipt.created_at));
  }

  function timeBucketMatchesAge(bucket, ageMs) {
    const range = timeBucketAgeRangeMs(bucket);
    if (range === null) return false;
    const toleranceMs = range.max - range.min >= 60 * 60 * 1000 ? 5 * 60 * 1000 : 2 * 60 * 1000;
    return ageMs >= Math.max(0, range.min - toleranceMs) && ageMs <= range.max + toleranceMs;
  }

  function feedItems(receipts, backendItems, county = null, nowMs = Date.now()) {
    const normalizedCounty = String(county || "").trim().toLowerCase();
    const matchedReceiptCounts = new Map();
    backendItems.filter((item) => item.is_own).forEach((item) => {
      const fingerprint = summaryFingerprint(item);
      matchedReceiptCounts.set(fingerprint, (matchedReceiptCounts.get(fingerprint) || 0) + 1);
    });
    const ownItems = receipts
      .filter((receipt) => {
        const fingerprint = summaryFingerprint(receipt.summary);
        const matchedCount = matchedReceiptCounts.get(fingerprint) || 0;
        if (matchedCount === 0) return true;
        matchedReceiptCounts.set(fingerprint, matchedCount - 1);
        return false;
      })
      .map((receipt) => ({
        ...receipt.summary,
        time_bucket: timeBucketFromAge(nowMs - receipt.created_at),
        is_own: true,
        receipt_created_at: receipt.created_at,
        feed_age_ms: Math.max(0, nowMs - receipt.created_at),
      }))
      .filter((item) => !normalizedCounty || item.location.label.toLowerCase() === normalizedCounty);
    const publicItems = backendItems
      .map((item, index) => {
        const safe = sanitizeSummary(item);
        const receiptCreatedAt = Number.isFinite(item?.receipt_created_at) ? item.receipt_created_at : null;
        return {
          ...safe,
          is_own: Boolean(item.is_own),
          receipt_created_at: receiptCreatedAt,
          feed_age_ms: timeBucketAgeMs(safe.time_bucket),
          feed_index: index,
        };
      })
      .filter((item) => !normalizedCounty || item.location.label.toLowerCase() === normalizedCounty);
    return [...ownItems, ...publicItems]
      .sort(compareFeedItems)
      .slice(0, 100);
  }

  function compareFeedItems(left, right) {
    const ageDifference = feedSortAge(left) - feedSortAge(right);
    if (ageDifference !== 0) return ageDifference;
    return feedSortIndex(left) - feedSortIndex(right);
  }

  function feedSortAge(item) {
    if (Number.isFinite(item?.feed_age_ms)) return item.feed_age_ms;
    return timeBucketAgeMs(item?.time_bucket);
  }

  function feedSortIndex(item) {
    if (Number.isFinite(item?.feed_index)) return item.feed_index;
    return 0;
  }

  function formatRow(summary, translate) {
    const categories = summary.categories || [];
    const icons = categories.map((category) => iconForCategory(category, summary)).filter(Boolean).join(" ");
    const location = locationText(summary.location, translate);
    const text = rowText(categories, summary, location, translate);
    return {
      icons,
      text,
      time: timeBucketText(summary.time_bucket, translate),
      own: summary.is_own ? translated(translate, "pulse_your_report") : "",
    };
  }

  function formatMobileRow(summary, translate) {
    const categories = summary.categories || [];
    return {
      icons: categories.map((category) => mobileIconForCategory(category, summary)).filter(Boolean).join(" "),
      summary: mobileSummaryText(categories, summary, translate),
      location: mobileLocationText(summary.location, translate),
      time: timeBucketText(summary.time_bucket, translate),
      own: summary.is_own ? translated(translate, "pulse_your_report") : "",
    };
  }

  function mobileSummaryText(categories, summary, translate) {
    if (categories.length > 1) return translated(translate, "pulse_mixed_report");
    if (categories[0] === "people") return humanConcern(summary, translate);
    if (categories[0] === "animals") return capitalize(animalConcern(summary, translate));
    if (categories[0] === "environment") return translated(translate, "pulse_environmental_concern");
    return translated(translate, "pulse_community_report");
  }

  function mobileLocationText(location, translate) {
    const label = String(location?.label || "").trim();
    if (location?.level === "county" && label.endsWith(" County")) {
      return label.slice(0, -" County".length);
    }
    return locationText(location, translate);
  }

  function locationText(location, translate) {
    const label = String(location?.label || "").trim();
    if (location?.level === "county" && label.endsWith(" County")) {
      return translated(translate, "pulse_county_name", {
        county: label.slice(0, -" County".length),
      });
    }
    return label || "Arizona";
  }

  function rowText(categories, summary, location, translate) {
    if (categories.length === 1) {
      if (categories[0] === "people") {
        return translated(translate, "pulse_row_in", {
          concern: humanConcern(summary, translate),
          location,
        });
      }
      if (categories[0] === "animals") {
        return translated(translate, "pulse_row_reported_in", {
          concern: capitalize(animalConcern(summary, translate)),
          location,
        });
      }
      if (categories[0] === "environment") {
        return translated(translate, "pulse_environment_reported_in", { location });
      }
    }
    if (categories.length === 2) {
      return translated(translate, "pulse_row_in", {
        concern: twoDomainConcern(categories, summary, translate),
        location,
      });
    }
    if (categories.length >= 3) {
      return translated(translate, "pulse_multiple_reported_in", { location });
    }
    return translated(translate, "pulse_community_concern_in", { location });
  }

  function twoDomainConcern(categories, summary, translate) {
    const hasPeople = categories.includes("people");
    const hasAnimals = categories.includes("animals");
    const hasEnvironment = categories.includes("environment");
    if (hasPeople && hasAnimals) {
      return translated(translate, "pulse_people_animals", {
        people: humanConcern(summary, translate),
        animal: animalConcern(summary, translate),
      });
    }
    if (hasPeople && hasEnvironment) {
      return translated(translate, "pulse_people_environment", {
        people: humanConcern(summary, translate),
      });
    }
    if (hasAnimals && hasEnvironment) {
      return translated(translate, "pulse_animals_environment", {
        animal: capitalize(animalLabel(summary, translate)),
      });
    }
    return translated(translate, "pulse_multiple_reported");
  }

  function humanConcern(summary, translate) {
    return translated(
      translate,
      summary.feeling === "good" ? "pulse_healthy_human" : "pulse_sick_human"
    );
  }

  function animalConcern(summary, translate) {
    return translated(translate, "pulse_animal_concern", {
      animal: animalLabel(summary, translate),
    });
  }

  function animalLabel(summary, translate) {
    const key = {
      livestock: "pulse_livestock",
      pet: "pulse_pet",
      wildlife: "pulse_wildlife",
    }[summary.animal_group] || "pulse_animal";
    return translated(translate, key);
  }

  function tooltipSections(summary, translate) {
    const sections = [];
    if (summary.categories.includes("people")) {
      const lines = [];
      if (summary.feeling === "good") {
        lines.push(translated(translate, "pulse_no_symptoms"));
      } else {
        if (summary.severity) {
          lines.push(translated(translate, "pulse_severity", {
            value: controlledLabel("pulse_severity", summary.severity, translate),
          }));
        }
        if (summary.symptoms.length) {
          lines.push(translated(translate, "pulse_symptoms", {
            value: conciseList(summary.symptoms, "pulse_symptom", translate),
          }));
        }
      }
      sections.push({ title: translated(translate, "pulse_human_health_report"), lines });
    }
    if (summary.categories.includes("animals")) {
      const lines = [];
      if (summary.animal_group) {
        lines.push(translated(translate, "pulse_group", {
          value: animalLabel(summary, translate),
        }));
      }
      if (summary.animal_type) {
        lines.push(translated(translate, "pulse_type", {
          value: controlledLabel("pulse_animal_type", summary.animal_type, translate),
        }));
      }
      if (summary.animal_sick_count_bucket) {
        lines.push(translated(translate, "pulse_sick_animals", {
          value: countBucketText(summary.animal_sick_count_bucket),
        }));
      }
      if (summary.animal_dead_count_bucket) {
        lines.push(translated(translate, "pulse_dead_animals", {
          value: countBucketText(summary.animal_dead_count_bucket),
        }));
      }
      sections.push({ title: translated(translate, "pulse_animal_health_report"), lines });
    }
    if (summary.categories.includes("environment")) {
      const lines = summary.environment_concerns.length
        ? [translated(translate, "pulse_concern", {
          value: conciseList(summary.environment_concerns, "pulse_environment", translate),
        })]
        : [];
      sections.push({ title: translated(translate, "pulse_environmental_report"), lines });
    }
    sections.push({
      title: "",
      lines: [
        translated(translate, "pulse_county", {
          value: locationText(summary.location, translate),
        }),
        translated(translate, "pulse_reported", {
          time: timeBucketText(summary.time_bucket, translate),
        }),
      ],
    });
    return sections;
  }

  function summaryFingerprint(summary) {
    const safe = sanitizeSummary(summary);
    return JSON.stringify({
      categories: safe.categories,
      feeling: safe.feeling,
      severity: safe.severity,
      symptoms: safe.symptoms,
      animal_type: safe.animal_type,
      animal_group: safe.animal_group,
      animal_sick_count_bucket: safe.animal_sick_count_bucket,
      animal_dead_count_bucket: safe.animal_dead_count_bucket,
      environment_concerns: safe.environment_concerns,
    });
  }

  function visibleFeedFingerprint(items) {
    const safeItems = Array.isArray(items) ? items : [];
    return JSON.stringify(safeItems.map((item) => ({
      ...sanitizeSummary(item),
      is_own: Boolean(item?.is_own),
    })));
  }

  function timeBucketFromAge(ageMs) {
    const elapsedSeconds = Math.max(0, Math.floor(ageMs / 1000));
    if (elapsedSeconds < 60) return "just_now";
    const minutes = Math.floor(elapsedSeconds / 60);
    if (minutes < 60) return `${minutes}_min_ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return hours === 1 ? "1_hour_ago" : `${hours}_hours_ago`;
    const days = Math.min(7, Math.floor(hours / 24));
    return days === 1 ? "yesterday" : `${days}_days_ago`;
  }

  function timeBucketText(bucket, translate) {
    if (bucket === "just_now") return translated(translate, "pulse_time_just_now");
    if (bucket === "recently") return translated(translate, "pulse_time_recently");
    if (bucket === "yesterday") return translated(translate, "pulse_time_yesterday");
    let match = String(bucket || "").match(/^(\d+)_min_ago$/);
    if (match) {
      return translated(
        translate,
        match[1] === "1" ? "pulse_time_minute_ago" : "pulse_time_minutes_ago",
        { count: match[1] }
      );
    }
    match = String(bucket || "").match(/^(\d+)_hours?_ago$/);
    if (match) {
      return translated(
        translate,
        match[1] === "1" ? "pulse_time_hour_ago" : "pulse_time_hours_ago",
        { count: match[1] }
      );
    }
    match = String(bucket || "").match(/^(\d+)_days_ago$/);
    if (match) {
      return translated(
        translate,
        match[1] === "1" ? "pulse_time_day_ago" : "pulse_time_days_ago",
        { count: match[1] }
      );
    }
    return translated(translate, "pulse_time_recently");
  }

  function timeBucketAgeMs(bucket) {
    if (bucket === "just_now" || bucket === "recently") return 0;
    if (bucket === "yesterday") return 24 * 60 * 60 * 1000;
    let match = String(bucket || "").match(/^(\d+)_min_ago$/);
    if (match) return Number.parseInt(match[1], 10) * 60 * 1000;
    match = String(bucket || "").match(/^(\d+)_hours?_ago$/);
    if (match) return Number.parseInt(match[1], 10) * 60 * 60 * 1000;
    match = String(bucket || "").match(/^(\d+)_days_ago$/);
    if (match) return Number.parseInt(match[1], 10) * 24 * 60 * 60 * 1000;
    return 0;
  }

  function timeBucketAgeRangeMs(bucket) {
    if (bucket === "just_now") return { min: 0, max: 60 * 1000 };
    if (bucket === "yesterday") return { min: 24 * 60 * 60 * 1000, max: 48 * 60 * 60 * 1000 };
    let match = String(bucket || "").match(/^(\d+)_min_ago$/);
    if (match) {
      const minutes = Number.parseInt(match[1], 10);
      return { min: minutes * 60 * 1000, max: (minutes + 1) * 60 * 1000 };
    }
    match = String(bucket || "").match(/^(\d+)_hours?_ago$/);
    if (match) {
      const hours = Number.parseInt(match[1], 10);
      return { min: hours * 60 * 60 * 1000, max: (hours + 1) * 60 * 60 * 1000 };
    }
    match = String(bucket || "").match(/^(\d+)_days_ago$/);
    if (match) {
      const days = Number.parseInt(match[1], 10);
      return { min: days * 24 * 60 * 60 * 1000, max: (days + 1) * 24 * 60 * 60 * 1000 };
    }
    return null;
  }

  function iconForCategory(category, summary) {
    if (category === "people") {
      if (summary.feeling === "good") return "🙂";
      return "🤒";
    }
    if (category === "animals") return ANIMAL_ICONS[summary.animal_group] || ANIMAL_ICONS.default;
    if (category === "environment") return "🌎";
    return "";
  }

  function mobileIconForCategory(category, summary) {
    if (category === "people" || category === "environment") return iconForCategory(category, summary);
    if (category === "animals") return MOBILE_ANIMAL_ICONS[summary.animal_group] || MOBILE_ANIMAL_ICONS.default;
    return "";
  }

  function conciseList(values, keyPrefix, translate, limit = 3) {
    const visible = values.slice(0, limit).map((value) => controlledLabel(keyPrefix, value, translate));
    const remaining = values.length - visible.length;
    const more = remaining > 0
      ? `, ${translated(translate, "pulse_more", { count: remaining })}`
      : "";
    return `${visible.join(", ")}${more}`;
  }

  function countBucket(value) {
    if (value === null || value === undefined || value === "" || typeof value === "boolean") return null;
    const count = Number.parseInt(value, 10);
    if (!Number.isInteger(count) || count < 0) return null;
    if (count === 0) return "0";
    if (count <= 5) return "1-5";
    if (count <= 20) return "6-20";
    return "21_plus";
  }

  function countBucketText(value) {
    return value === "21_plus" ? "21+" : value;
  }

  function sanitizeLocation(location) {
    const label = String(location?.label || "").trim();
    if (location?.level === "county" && /^[A-Za-z ]+ County$/.test(label)) {
      return { level: "county", label };
    }
    return { level: "state", label: "Arizona" };
  }

  function sanitizeTimeBucket(value) {
    const text = String(value || "");
    if (["just_now", "recently", "yesterday"].includes(text)) return text;
    if (/^\d+_(min|hours?|days)_ago$/.test(text)) return text;
    return "recently";
  }

  function allowedList(values, allowed) {
    if (!Array.isArray(values)) return [];
    const safeValues = [];
    values.forEach((value) => {
      const safe = allowed.get(normalizationKey(value));
      if (safe && !safeValues.includes(safe)) safeValues.push(safe);
    });
    return safeValues;
  }

  function allowedSetValue(value, allowed) {
    const normalized = normalizationKey(value).replaceAll(" ", "_");
    return allowed.has(normalized) ? normalized : null;
  }

  function allowedLiteralValue(value, allowed) {
    const normalized = typeof value === "string" ? value.trim().toLowerCase() : "";
    return allowed.has(normalized) ? normalized : null;
  }

  function normalizationKey(value) {
    return typeof value === "string"
      ? value.trim().toLowerCase().replaceAll("_", " ").replaceAll("-", " ").replace(/\s+/g, " ")
      : "";
  }

  function humanize(value) {
    return String(value || "").replaceAll("_", " ");
  }

  function controlledLabel(keyPrefix, value, translate) {
    return translated(translate, `${keyPrefix}_${String(value || "")}`) || humanize(value);
  }

  function translated(translate, key, replacements = {}) {
    if (typeof translate !== "function") {
      throw new TypeError("Community Pulse formatting requires a translation function.");
    }
    return String(translate(key, replacements) || "");
  }

  function capitalize(value) {
    const text = humanize(value);
    return text ? `${text[0].toUpperCase()}${text.slice(1)}` : "";
  }

  const api = {
    RECEIPT_STORAGE_KEY,
    buildOptimisticSummary,
    createReceipt,
    feedItems,
    formatMobileRow,
    formatRow,
    loadReceipts,
    prependReceipts,
    reconcileReceipts,
    saveReceipts,
    summaryFingerprint,
    timeBucketText,
    tooltipSections,
    visibleFeedFingerprint,
  };

  if (typeof globalThis !== "undefined") {
    globalThis.CommunityPulse = api;
  }

  export {
    RECEIPT_STORAGE_KEY,
    buildOptimisticSummary,
    createReceipt,
    feedItems,
    formatMobileRow,
    formatRow,
    loadReceipts,
    prependReceipts,
    reconcileReceipts,
    saveReceipts,
    summaryFingerprint,
    timeBucketText,
    tooltipSections,
    visibleFeedFingerprint,
  };

  export default api;
